import React, { useState } from "react";
import {
    HiOutlineChatAlt,
    HiOutlinePaperAirplane,
    HiOutlineCheck,
    HiOutlineX,
    HiOutlineClock,
    HiOutlineExclamationCircle,
} from "react-icons/hi";
import {
    collection,
    addDoc,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useSelector } from "react-redux";
import "./FloatingFeedbackButton.scss";

const FloatingFeedbackButton = () => {
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedbackTitle, setFeedbackTitle] = useState("");
    const [feedbackDescription, setFeedbackDescription] = useState("");
    const [feedbackCategory, setFeedbackCategory] = useState("bug");
    const [feedbackSeverity, setFeedbackSeverity] = useState("medium");
    const [feedbackModule, setFeedbackModule] = useState("general");
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const { user } = useSelector((state) => state.auth);

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();

        if (!feedbackTitle.trim() || !feedbackDescription.trim()) {
            setSubmitError(
                "Please provide both a title and description for your feedback."
            );
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            await addDoc(collection(db, "userTestingFeedbacks"), {
                title: feedbackTitle,
                description: feedbackDescription,
                category: feedbackCategory,
                severity: feedbackSeverity,
                module: feedbackModule,
                status: "pending",
                userEmail: user.email,
                userName: user.displayName || user.email.split("@")[0],
                createdAt: serverTimestamp(),
                adminResponse: null,
                adminResponseDate: null,
            });

            // Success
            setSubmitSuccess(true);
            setFeedbackTitle("");
            setFeedbackDescription("");
            setFeedbackCategory("bug");
            setFeedbackSeverity("medium");
            setFeedbackModule("general");

            // Clear success message and close form after 3 seconds
            setTimeout(() => {
                setSubmitSuccess(false);
                setShowFeedbackForm(false);
            }, 3000);
        } catch (error) {
            console.error("Error submitting feedback:", error);
            setSubmitError("Failed to submit feedback. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form when closing
    const handleCloseFeedbackForm = () => {
        setShowFeedbackForm(false);
        setFeedbackTitle("");
        setFeedbackDescription("");
        setFeedbackCategory("bug");
        setFeedbackSeverity("medium");
        setFeedbackModule("general");
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    // Don't show the button if user is not authenticated
    if (!user) {
        return null;
    }

    return (
        <>
            {/* Floating Feedback Button */}
            <button
                className="floating-feedback-button"
                onClick={() => setShowFeedbackForm(true)}
                title="Send Feedback"
            >
                <HiOutlineChatAlt />
                <span>Feedback</span>
            </button>

            {/* Feedback Form Popup */}
            {showFeedbackForm && (
                <div className="feedback-popup-overlay">
                    <div className="feedback-popup">
                        <div className="popup-header">
                            <h2>Submit Feedback</h2>
                            <button
                                className="close-button"
                                onClick={handleCloseFeedbackForm}
                            >
                                <HiOutlineX />
                            </button>
                        </div>

                        <div className="popup-content">
                            <p>
                                Share your feedback, report issues, or suggest
                                improvements for the PPC Portal.
                            </p>

                            <form
                                className="feedback-form"
                                onSubmit={handleSubmitFeedback}
                            >
                                <div className="form-group">
                                    <label htmlFor="feedbackTitle">Title</label>
                                    <input
                                        type="text"
                                        id="feedbackTitle"
                                        value={feedbackTitle}
                                        onChange={(e) =>
                                            setFeedbackTitle(e.target.value)
                                        }
                                        placeholder="Brief description of your feedback"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="feedbackCategory">
                                        Category
                                    </label>
                                    <select
                                        id="feedbackCategory"
                                        value={feedbackCategory}
                                        onChange={(e) =>
                                            setFeedbackCategory(e.target.value)
                                        }
                                    >
                                        <option value="bug">Bug Report</option>
                                        <option value="feature">
                                            Feature Request
                                        </option>
                                        <option value="improvement">
                                            Improvement Suggestion
                                        </option>
                                        <option value="question">
                                            Question
                                        </option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="feedbackModule">
                                        Module
                                    </label>
                                    <select
                                        id="feedbackModule"
                                        value={feedbackModule}
                                        onChange={(e) =>
                                            setFeedbackModule(e.target.value)
                                        }
                                    >
                                        <option value="general">General</option>
                                        <option value="profile">Profile</option>
                                        <option value="documents">
                                            Documents
                                        </option>
                                        <option value="payslips">
                                            Payslips
                                        </option>
                                        <option value="tasks">
                                            Tasks & Performance
                                        </option>
                                        <option value="leave">
                                            Leave Management
                                        </option>
                                        <option value="claims">Claims</option>
                                        <option value="remote-work">
                                            Remote Work
                                        </option>
                                        <option value="admin">
                                            Admin Features
                                        </option>
                                    </select>
                                </div>

                                {feedbackCategory === "bug" && (
                                    <div className="form-group">
                                        <label htmlFor="feedbackSeverity">
                                            Severity
                                        </label>
                                        <select
                                            id="feedbackSeverity"
                                            value={feedbackSeverity}
                                            onChange={(e) =>
                                                setFeedbackSeverity(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">
                                                Medium
                                            </option>
                                            <option value="high">High</option>
                                            <option value="critical">
                                                Critical
                                            </option>
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="feedbackDescription">
                                        Description
                                    </label>
                                    <textarea
                                        id="feedbackDescription"
                                        value={feedbackDescription}
                                        onChange={(e) =>
                                            setFeedbackDescription(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Provide detailed information about your feedback"
                                        rows="5"
                                        required
                                    ></textarea>
                                </div>

                                {submitError && (
                                    <div className="error-message">
                                        <HiOutlineExclamationCircle />
                                        <span>{submitError}</span>
                                    </div>
                                )}

                                {submitSuccess && (
                                    <div className="success-message">
                                        <HiOutlineCheck />
                                        <span>
                                            Feedback submitted successfully!
                                        </span>
                                    </div>
                                )}

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={handleCloseFeedbackForm}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-button"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <HiOutlineClock className="spinning" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlinePaperAirplane />
                                                Submit Feedback
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FloatingFeedbackButton;